<template>
  <div>
    <div
      tabindex="0"
      ref="npsInputRail"
      class="nps_input"
      @keyup="keyHandler"
      @mouseleave="hover = undefined"
      v-touch:moving="touchMoveHandler"
      v-touch:end="touchEndHandler"
    >
      <div
        class="nps_input__option"
        :class="{
          'nps_input__option--selected': value === option,
          'nps_input__option--hover': option === hover,
          'nps_input__option--left_of_hover': option === hover - 1,
          'nps_input__option--right_of_hover': option === hover + 1,
        }"
        v-for="option in options"
        :data-value="option"
        :key="option"
        @mouseup="
          inputValue = option;
          hover = undefined;
        "
        @mouseover="hover = option"
        ref="options"
      >
        <div
          class="nps_input__option_box"
          :class="{
            'nps_input__option_box--green': option <= 10 && option > 8,
            'nps_input__option_box--orange': option <= 8 && option > 6,
            'nps_input__option_box--red': option <= 6,
          }"
        >
          {{ option }}
        </div>
      </div>
    </div>
    <ClearButton @clear="clearValue" v-if="!required && value" />
    <ValidationMessage
      :displayInvalid="displayInvalid"
      :validation-message="externalValidationErrorMessage || validationMessage"
    />
  </div>
</template>

<script>
import isMobile from "is-mobile";
import BaseComponent from "../../BaseComponent";
import globalComponentSettings from "../../../../global-component-settings";
import InputBase from "./mixins/InputBase";
import ClearButton from "../ClearButton.vue";

/**
 * @group Inputs
 * This is our NPS Input, on mobile it's dragable, on desktop it's not.
 * Users indicate if they would promote the webshop to other consumers.
 */
export default {
  mixins: [BaseComponent, InputBase],
  name: "NpsInput",
  components: {
    ClearButton,
  },
  data() {
    return {
      options: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      hover: undefined,
      touchEventStore: {
        elementOffsetLeft: undefined,
        closestToTouch: undefined,
      },
    };
  },
  props: {
    required: {
      type: Boolean,
      default: false,
    },
    validationMessage: {
      type: String,
      default: "Please select an option",
    },
    enableExternalValidation: {
      type: Boolean,
      default: false,
    },
    externalValidationError: {
      type: Boolean,
    },
    externalValidationErrorMessage: {
      type: String,
    },
  },
  computed: {
    isMobile() {
      return isMobile();
    },
    displayInvalid() {
      if (this.enableExternalValidation) return this.externalValidationError;
      return this.isShowingValidationMessage && !this.isValid;
    },
    isValid() {
      return (
        (this.required &&
          this.value !== null &&
          this.value !== "" &&
          this.value !== undefined) ||
        !this.required
      );
    },
  },
  methods: {
    /**
     * @vuese
     * On mobile the user can drag the value around. That's what this function handles.
     * The function calculates where the numbers are and where the user is dragging.
     * Then highlights the number the user is dragging over.
     * When the user stops panning that number is selected.
     */
    touchMoveHandler(event) {
      if (!this.isMobile || !globalComponentSettings.npsInputTouchEnabled)
        return;
      if (typeof window === "undefined") return;
      let closest = this.touchEventStore.closestToTouch;
      window.requestAnimationFrame(() => {
        const goal = event.targetTouches[0].pageX;
        this.touchEventStore.closestToTouch = this.$refs.options.reduce(
          (previous, current) => {
            closest = previous;
            if (
              Math.abs(current.offsetLeft - goal) <
              Math.abs(previous.offsetLeft - goal)
            ) {
              closest = current;
            }
            return closest;
          }
        );
        this.hover = parseInt(
          this.touchEventStore.closestToTouch.dataset.value,
          10
        );
      });
    },
    touchEndHandler() {
      if (!this.isMobile || !globalComponentSettings.npsInputTouchEnabled)
        return;
      if (typeof window === "undefined") return;
      if (!this.hover) return;
      if (typeof window === "undefined") return;
      window.requestAnimationFrame(() => {
        this.inputValue = this.hover;
        this.hover = undefined;
      });
    },
    keyHandler(event) {
      // @vuese
      // When the NpsInput is selected, pressing 0-9 will set it's value to the key pressed.
      // Pressing arrow left / right will decrement / increment.
      // @arg is the keyHandler return value.
      const numeric = event.key * 1;
      const max = 10;

      if (!Number.isNaN(Number(event.key))) {
        if (numeric <= max && numeric > -1) {
          this.inputValue = numeric;
        }
      } else if (event.key === "ArrowRight") {
        if (this.value !== "" && this.inputValue + 1 <= max) {
          this.inputValue += 1;
        } else {
          this.inputValue = 0;
        }
      } else if (event.key === "ArrowLeft") {
        if (this.value && this.inputValue - 1 > -1) {
          this.inputValue -= 1;
        } else {
          this.inputValue = max;
        }
      }
    },
    // @vuese
    clearValue() {
      this.inputValue = "";
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../../style_variables/style_variables.scss";

$mobile_option_hover_height: 91px;
$mobile_option_next_to_hover_height: 44px;
$mobile_option_height: 36px;
$animation_speed: 175ms;

.nps_input {
  @extend %button_typography_style;
  height: 72px;
  display: flex;
  justify-content: space-around;
  align-items: center;
  border-radius: 8px;
  padding: 0px 10px;
  max-width: $input_max_width;
  @media (hover: none) and (pointer: coarse) {
    widht: 100%;
    min-width: 304px;
    max-width: 476px;
    border: 1px solid $grey_french;
    background-color: $white;
  }
  @media (hover: hover) {
    max-width: 650px;
    padding: 0px;
    justify-content: space-between;
  }
}
.nps_input__option {
  transition: all $animation_speed;
  position: relative;
  z-index: 2;
  width: 32px;
  height: 36px;
  will-change: width, height;
  box-sizing: content-box;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  @media (hover: hover) {
    width: 56px;
    height: 56px;

    &.nps_input__option--hover {
      transform: translateY(-8px);
    }
    &.nps_input__option--selected,
    &.nps_input__option--hover {
      cursor: pointer;
      .nps_input__option_box--red {
        color: $red;
      }
      .nps_input__option_box--orange {
        color: $orange;
      }
      .nps_input__option_box--green {
        color: $green;
      }
    }
    &.nps_input__option--selected {
      .nps_input__option_box--red {
        border: 1px solid $red;
        background-color: rgba($red, 0.05);
      }
      .nps_input__option_box--orange {
        border: 1px solid $orange;
        background-color: rgba($orange, 0.05);
      }
      .nps_input__option_box--green {
        border: 1px solid $green;
        background-color: rgba($green, 0.05);
      }
    }
  }
  @media (hover: none) and (pointer: coarse) {
    &.nps_input__option--left_of_hover,
    &.nps_input__option--right_of_hover {
      width: $mobile_option_height;
      height: $mobile_option_next_to_hover_height;
      transform: translateY(
        -#{($mobile_option_next_to_hover_height - $mobile_option_height) / 2}
      );
      .nps_input__option_box {
        z-index: 2;
      }
    }
    &.nps_input__option--hover {
      width: 46px;
      z-index: 4;
      height: $mobile_option_hover_height;
      transform: translateY(
        -#{($mobile_option_hover_height - $mobile_option_height) / 2}
      );
      .nps_input__option_box {
        &:before {
          opacity: 1;
        }
      }
    }
    &.nps_input__option--hover,
    &.nps_input__option--selected {
      .nps_input__option_box {
        &.nps_input__option_box--red {
          color: $red;
        }
        &.nps_input__option_box--orange {
          color: $orange;
        }
        &.nps_input__option_box--green {
          color: $green;
        }
      }
    }
  }
}
.nps_input__option_box {
  transition: all $animation_speed;
  will-change: width, height;
  width: 100%;
  height: 100%;
  background-color: $white;
  border: 1px solid rgba($grey_athens, 0);
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 4px;
  font-size: 22px;
  font-weight: 400;
  will-change: width;
  @media (hover: hover) {
    width: 100%;
    padding: 0px;
    background-color: $white;
    border: 1px solid $grey_french;
    border-radius: 4px;
    align-items: center;
  }
  &:before {
    transition: all $animation_speed;
    content: "";
    opacity: 0;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: -1;
    border-radius: 8px;
    box-shadow: 0 6px 14px 0 rgba(0, 0, 0, 0.15);
  }
}
</style>
